import { useState, useCallback, useMemo } from 'react';
import { createField, createRole, generateId } from '../utils/fieldTypes';
import { clampToPage, enforceMinimumSize } from '../utils/coordinateUtils';
import FIELD_TYPES from '../utils/fieldTypes';

const DEFAULT_ROLES = [
  { uuid: generateId(), name: 'Client', color: '#3B82F6' },
];

/**
 * Main state management hook for the template editor.
 * Handles all template state including PDF, fields, roles, and UI state.
 */
const useTemplateEditor = (initialTemplate = null) => {
  // Mode: 'create' or 'edit'
  const [mode] = useState(initialTemplate ? 'edit' : 'create');
  const [templateId] = useState(initialTemplate?.id || null);

  // PDF state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(initialTemplate?.documentUrl || null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });

  // Template metadata
  const [templateName, setTemplateName] = useState(initialTemplate?.name || '');
  const [folderName, setFolderName] = useState(initialTemplate?.folder_name || '');

  // Submitter roles
  const [roles, setRoles] = useState(
    initialTemplate?.submitters?.map((s, i) => ({
      uuid: s.uuid || generateId(),
      name: s.name,
      color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'][i % 4],
    })) || DEFAULT_ROLES
  );

  // Fields
  const [fields, setFields] = useState(initialTemplate?.fields || []);

  // UI state
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [activeRoleUuid, setActiveRoleUuid] = useState(roles[0]?.uuid || null);

  // Loading/error state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Get the currently selected field
  const selectedField = useMemo(() => {
    return fields.find(f => f.uuid === selectedFieldId) || null;
  }, [fields, selectedFieldId]);

  // Get fields for the current page
  const currentPageFields = useMemo(() => {
    return fields.filter(f => f.areas.some(a => a.page === currentPage));
  }, [fields, currentPage]);

  // Load a PDF file
  const loadPDF = useCallback(async (file) => {
    setPdfFile(file);
    setPdfUrl(null);
    setCurrentPage(1);
    setFields([]);
    setSelectedFieldId(null);

    // Convert to base64 for API submission
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setPdfBase64(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle document load
  const handleDocumentLoad = useCallback(({ numPages }) => {
    setTotalPages(numPages);
  }, []);

  // Handle page load
  const handlePageLoad = useCallback(({ width, height }) => {
    setPageDimensions({ width, height });
  }, []);

  // Navigation
  const goToPage = useCallback((pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setSelectedFieldId(null); // Deselect when changing pages
    }
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const goToNextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  // Role management
  const addRole = useCallback((name) => {
    const newRole = createRole(name, roles);
    setRoles(prev => [...prev, newRole]);
    return newRole;
  }, [roles]);

  const updateRole = useCallback((uuid, updates) => {
    setRoles(prev => prev.map(r =>
      r.uuid === uuid ? { ...r, ...updates } : r
    ));
  }, []);

  const removeRole = useCallback((uuid) => {
    // Don't allow removing the last role
    if (roles.length <= 1) return;

    setRoles(prev => prev.filter(r => r.uuid !== uuid));

    // Update active role if needed
    if (activeRoleUuid === uuid) {
      setActiveRoleUuid(roles.find(r => r.uuid !== uuid)?.uuid || null);
    }

    // Remove fields assigned to this role or reassign them
    setFields(prev => prev.filter(f => f.roleUuid !== uuid));
  }, [roles, activeRoleUuid]);

  // Field management
  const addField = useCallback((type, position) => {
    if (!activeRoleUuid) {
      setError('Please select a role before adding fields');
      return null;
    }

    const newField = createField(type, position, currentPage, activeRoleUuid);
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.uuid);
    return newField;
  }, [currentPage, activeRoleUuid]);

  const updateField = useCallback((fieldId, updates) => {
    setFields(prev => prev.map(f =>
      f.uuid === fieldId ? { ...f, ...updates } : f
    ));
  }, []);

  const moveField = useCallback((fieldId, newPosition) => {
    setFields(prev => prev.map(f => {
      if (f.uuid !== fieldId) return f;

      const area = f.areas[0]; // Assuming single area per field for now

      const newArea = clampToPage({
        ...area,
        x: newPosition.x,
        y: newPosition.y,
      });

      return {
        ...f,
        areas: [newArea],
      };
    }));
  }, []);

  const resizeField = useCallback((fieldId, newDimensions) => {
    setFields(prev => prev.map(f => {
      if (f.uuid !== fieldId) return f;

      const fieldType = FIELD_TYPES[f.type];
      const area = f.areas[0];

      let newArea = {
        ...area,
        ...newDimensions,
      };

      // Enforce minimum size
      newArea = enforceMinimumSize(newArea, fieldType.minWidth, fieldType.minHeight);
      newArea = clampToPage(newArea);

      return {
        ...f,
        areas: [newArea],
      };
    }));
  }, []);

  const deleteField = useCallback((fieldId) => {
    setFields(prev => prev.filter(f => f.uuid !== fieldId));
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  }, [selectedFieldId]);

  const duplicateField = useCallback((fieldId) => {
    const field = fields.find(f => f.uuid === fieldId);
    if (!field) return null;

    const newField = {
      ...field,
      uuid: generateId(),
      name: `${field.name} (copy)`,
      areas: field.areas.map(a => ({
        ...a,
        x: Math.min(a.x + 0.02, 1 - a.w), // Offset slightly
        y: Math.min(a.y + 0.02, 1 - a.h),
      })),
    };

    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.uuid);
    return newField;
  }, [fields]);

  const selectField = useCallback((fieldId) => {
    setSelectedFieldId(fieldId);
  }, []);

  const deselectField = useCallback(() => {
    setSelectedFieldId(null);
  }, []);

  // Build the template data for API submission
  const buildTemplatePayload = useCallback(() => {
    if (!templateName.trim()) {
      throw new Error('Template name is required');
    }
    if (fields.length === 0) {
      throw new Error('At least one field is required');
    }
    if (!pdfBase64 && !pdfUrl) {
      throw new Error('PDF document is required');
    }

    // Map fields to API format
    const apiFields = fields.map(f => {
      const role = roles.find(r => r.uuid === f.roleUuid);
      return {
        name: f.name,
        type: f.type,
        role: role?.name || 'Signer',
        required: f.required,
        areas: f.areas.map(a => ({
          x: a.x,
          y: a.y,
          w: a.w,
          h: a.h,
          page: a.page,
        })),
      };
    });

    const payload = {
      name: templateName.trim(),
      folder_name: folderName.trim() || undefined,
      documents: [{
        name: pdfFile?.name || 'document.pdf',
        file: pdfBase64,
        fields: apiFields,
      }],
    };

    return payload;
  }, [templateName, folderName, fields, roles, pdfBase64, pdfFile, pdfUrl]);

  // Build update payload for existing templates
  const buildUpdatePayload = useCallback(() => {
    if (!templateName.trim()) {
      throw new Error('Template name is required');
    }

    const apiFields = fields.map(f => {
      const role = roles.find(r => r.uuid === f.roleUuid);
      return {
        uuid: f.uuid,
        name: f.name,
        type: f.type,
        role: role?.name || 'Signer',
        required: f.required,
        areas: f.areas,
      };
    });

    return {
      name: templateName.trim(),
      folder_name: folderName.trim() || undefined,
      fields: apiFields,
      submitters: roles.map(r => ({ name: r.name })),
    };
  }, [templateName, folderName, fields, roles]);

  // Validation
  const validationErrors = useMemo(() => {
    const errors = [];
    if (!templateName.trim()) {
      errors.push('Template name is required');
    }
    if (!pdfFile && !pdfUrl) {
      errors.push('PDF document is required');
    }
    if (fields.length === 0) {
      errors.push('At least one field is required');
    }
    const unassignedFields = fields.filter(f => !f.roleUuid);
    if (unassignedFields.length > 0) {
      errors.push('All fields must be assigned to a role');
    }
    return errors;
  }, [templateName, pdfFile, pdfUrl, fields]);

  const isValid = validationErrors.length === 0;

  // Reset editor
  const reset = useCallback(() => {
    setPdfFile(null);
    setPdfUrl(null);
    setPdfBase64(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPageDimensions({ width: 0, height: 0 });
    setTemplateName('');
    setFolderName('');
    setRoles(DEFAULT_ROLES);
    setFields([]);
    setSelectedFieldId(null);
    setActiveRoleUuid(DEFAULT_ROLES[0].uuid);
    setError(null);
  }, []);

  return {
    // Mode
    mode,
    templateId,

    // PDF state
    pdfFile,
    pdfUrl,
    pdfBase64,
    currentPage,
    totalPages,
    pageDimensions,
    loadPDF,
    handleDocumentLoad,
    handlePageLoad,

    // Navigation
    goToPage,
    goToPreviousPage,
    goToNextPage,

    // Template metadata
    templateName,
    setTemplateName,
    folderName,
    setFolderName,

    // Roles
    roles,
    activeRoleUuid,
    setActiveRoleUuid,
    addRole,
    updateRole,
    removeRole,

    // Fields
    fields,
    currentPageFields,
    selectedFieldId,
    selectedField,
    addField,
    updateField,
    moveField,
    resizeField,
    deleteField,
    duplicateField,
    selectField,
    deselectField,

    // Validation
    validationErrors,
    isValid,

    // Loading state
    isLoading,
    setIsLoading,
    isSaving,
    setIsSaving,
    error,
    setError,

    // API helpers
    buildTemplatePayload,
    buildUpdatePayload,
    reset,
  };
};

export default useTemplateEditor;
